import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { paymentApi } from '@formosa/shared/api/payment';
import { ApiError } from '@formosa/shared/api/request';
import CurrencyPickerSheet from '../../components/deposit/CurrencyPickerSheet';
import {
  DEFAULT_QUICK_AMOUNTS,
  calcRebateAmount,
  formatAmountRange,
  formatDepositAmount,
  isAmountInRange,
  resolveImageUrl,
} from '../../constants/depositConstants';
import { tf } from '../../constants/fundConstants';
import { useTranslation } from '../../i18n/LocaleContext';

export default function OnlineDepositPage() {
  const navigate = useNavigate();
  const { merchantId } = useParams();
  const location = useLocation();
  const { t } = useTranslation();

  const [merchant, setMerchant] = useState(location.state?.merchant || null);
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [amount, setAmount] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const selectedChannel = useMemo(
    () => channels.find((item) => item.channelId === selectedChannelId) || null,
    [channels, selectedChannelId]
  );

  const rebateAmount = useMemo(
    () => calcRebateAmount(amount, selectedChannel?.rechargeRebate),
    [amount, selectedChannel?.rechargeRebate]
  );

  const canSubmit = useMemo(
    () => selectedChannel
      && isAmountInRange(amount, selectedChannel.rechargeMin, selectedChannel.rechargeMax)
      && !submitting,
    [amount, selectedChannel, submitting]
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const rows = await paymentApi.listOnlineDepositChannels({ merchantId });
        const list = Array.isArray(rows) ? rows : [];
        if (!cancelled) {
          setChannels(list);
          setSelectedChannelId(list[0]?.channelId || null);
        }
      } catch (err) {
        if (!cancelled) {
          setChannels([]);
          setError(err.message || t('deposit.loadChannelsFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [merchantId, t]);

  useEffect(() => {
    if (merchant || !merchantId) {
      return;
    }
    paymentApi.listDepositMerchants().then((rows) => {
      const matched = (rows || []).find(
        (item) => item.kind === 'ONLINE' && String(item.id) === String(merchantId)
      );
      if (matched) {
        setMerchant(matched);
      }
    }).catch(() => {});
  }, [merchant, merchantId]);

  function handleQuickAmount(value) {
    setAmount(String(value));
  }

  function handleUseAll() {
    if (selectedChannel?.rechargeMax) {
      setAmount(String(selectedChannel.rechargeMax));
    }
  }

  async function handleSubmit() {
    if (!selectedChannel || !merchant?.merchantCode) {
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const result = await paymentApi.createOnlineDeposit({
        realAmount: Number(amount),
        currencyName: selectedChannel.currencyName,
        merchantCode: merchant.merchantCode,
        channelId: selectedChannel.channelId,
      });
      setSuccess(t('deposit.onlineSubmitSuccess'));
      if (result?.payUrl) {
        window.open(result.payUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('deposit.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="deposit-page deposit-page--form">
      <header className="deposit-page__header">
        <button
          type="button"
          className="deposit-page__back"
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
        >
          ‹
        </button>
        <h1>{t('deposit.onlineTitle')}</h1>
        <span className="deposit-page__header-spacer" />
      </header>

      {loading && <p className="deposit-page__status">{t('common.loading')}</p>}
      {error && <p className="deposit-page__error">{error}</p>}
      {success && <p className="deposit-page__success">{success}</p>}

      {!loading && selectedChannel && (
        <>
          <section className="deposit-field">
            <div className="deposit-field__label">{t('deposit.currency')}</div>
            <button
              type="button"
              className="deposit-currency-select"
              onClick={() => setPickerOpen(true)}
            >
              <span className="deposit-currency-select__left">
                {selectedChannel.image ? (
                  <img src={resolveImageUrl(selectedChannel.image)} alt="" />
                ) : (
                  <span className="deposit-currency-select__fallback">₮</span>
                )}
                <strong>{selectedChannel.currencyName || selectedChannel.label}</strong>
              </span>
              <span className="deposit-currency-select__right">
                {tf(t, 'deposit.chainName', { chain: selectedChannel.chainName || '—' })}
                <span aria-hidden>▾</span>
              </span>
            </button>
          </section>

          <section className="deposit-quick-amounts">
            <h2>{t('deposit.quickAmounts')}</h2>
            <div className="deposit-quick-amounts__grid">
              {DEFAULT_QUICK_AMOUNTS.map((value) => (
                <button
                  key={value}
                  type="button"
                  className="deposit-quick-amounts__btn"
                  onClick={() => handleQuickAmount(value)}
                >
                  {value}
                </button>
              ))}
            </div>
          </section>

          <section className="deposit-amount-card">
            <div className="deposit-amount-card__title">{t('deposit.amountTitle')}</div>
            <div className="deposit-amount-card__body">
              <div className="deposit-amount-card__hint">
                {tf(t, 'deposit.rechargeRange', {
                  range: formatAmountRange(
                    selectedChannel.rechargeMin,
                    selectedChannel.rechargeMax
                  ),
                })}
              </div>
              <div className="deposit-amount-card__input-row">
                <span className="deposit-amount-card__symbol">
                  {selectedChannel.image ? (
                    <img src={resolveImageUrl(selectedChannel.image)} alt="" />
                  ) : (
                    '₮'
                  )}
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="0"
                />
                <button type="button" className="deposit-amount-card__all" onClick={handleUseAll}>
                  {t('deposit.all')}
                </button>
              </div>
              <div className="deposit-amount-card__approx">
                ≈{formatDepositAmount(amount)} ({selectedChannel.currencyName || 'USDT'})
              </div>
              <div className="deposit-amount-card__estimate">
                <span>{t('deposit.estimatedArrival')}</span>
                <strong>{formatDepositAmount(amount)}</strong>
              </div>
              <div className="deposit-amount-card__approx deposit-amount-card__approx--right">
                ≈{formatDepositAmount(amount)}({selectedChannel.currencyName || 'USDT'})
              </div>
            </div>
          </section>

          <div className="deposit-footnotes">
            <p>{t('deposit.rateHint')}</p>
            <p>
              {tf(t, 'deposit.rebateRate', {
                rate: formatDepositAmount(selectedChannel.rechargeRebate || 0, 0),
              })}
            </p>
            <p>
              {tf(t, 'deposit.rebateAmount', {
                coin: selectedChannel.currencyName || 'USDT',
                amount: formatDepositAmount(rebateAmount),
              })}
            </p>
          </div>

          <button
            type="button"
            className={`deposit-submit ${canSubmit ? 'is-active' : ''}`}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {submitting ? t('common.loading') : t('deposit.submit')}
          </button>
        </>
      )}

      {!loading && !selectedChannel && !error && (
        <p className="deposit-page__empty">{t('deposit.noChannels')}</p>
      )}

      <CurrencyPickerSheet
        open={pickerOpen}
        title={t('deposit.selectCurrency')}
        options={channels}
        selectedId={selectedChannelId}
        onClose={() => setPickerOpen(false)}
        onConfirm={(nextId) => {
          setSelectedChannelId(nextId);
          setPickerOpen(false);
        }}
      />
    </div>
  );
}
